import {
    baseActions,
    BaseActionsProps,
    baseMutations,
    baseState,
    BaseState,
    fetchItems,
    FilterParams,
} from "@/store/modules/helpers/baseState";
import {
    subsetGetter,
    SubsetGetterProps,
    Subsets,
    subsetSetter,
    subsetUpdateSetter,
} from "@/store/modules/helpers/baseSubset";
import { resetDelayedResetTimeout } from "@/store/modules/helpers/delayedReset";
import cloneDeep from "lodash/cloneDeep";
import { ActionReturn } from "@/components/forms/Form/types";
import { klinikenApi, getConfig } from "@/api";

export type Kontaktorsak = {
    id: number;
    code: string;
    displayName: string;
    config: {
        fields: Record<
            string,
            {
                required: boolean;
                visible: boolean;
            }
        >;
        groups: Record<string, { visible: boolean }>;
    };
};

export type KontaktorsakDisplay = {
    value: Kontaktorsak["id"];
    label: string;
    config: {
        fields: Record<
            string,
            {
                required: boolean;
                visible: boolean;
            }
        >;
        groups: Record<string, { visible: boolean }>;
    };
};

type State = BaseState & {
    kontaktorsak: Map<Kontaktorsak["id"], Kontaktorsak>;
};

type Getters = {
    kontaktorsakById: (data: { id: Kontaktorsak["id"] }) => Kontaktorsak | null;
};

export const convertKontaktorsakData: (args: { kontaktorsak?: Kontaktorsak }) => KontaktorsakDisplay | null = ({
    kontaktorsak,
}) => {
    if (!kontaktorsak) {
        return null;
    }

    return {
        value: kontaktorsak.id,
        label: kontaktorsak.displayName,
        config: kontaktorsak.config,
    };
};

/**
 * Varje default state ska innehålla BaseState + det som är specifikt för den här modulen.
 * Se helpers/Basestate
 */
const getDefaultState: () => State = () => {
    return {
        kontaktorsak: new Map(),
        ...cloneDeep(baseState),
    };
};

const moduleName = "kontaktorsak";

export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        kontaktorsak: (state: State) => [...state.kontaktorsak.values()],
        kontaktorsakSubset: (state: State) => (props: SubsetGetterProps<State> & { displayMode?: boolean }) => {
            const result = subsetGetter<State>({
                ...props,
                state,
                limit: 0,
                collectionName: moduleName,
            });

            if (props.displayMode && Array.isArray(result)) {
                return result.map((singleResult) => convertKontaktorsakData({ kontaktorsak: singleResult }));
            }

            return result;
        },
        kontaktorsakById:
            (state: State) =>
            ({ id }: { id?: number }) => {
                if (!id) {
                    return null;
                }

                return state.kontaktorsak.get(id);
            },
    },
    /**
     * Mutations ska alltid innehålla baseMutations + det som är specifikt för modulen
     */
    mutations: {
        ...baseMutations({
            reset: (state: State) => {
                Object.assign(state, getDefaultState());

                resetDelayedResetTimeout({ moduleName });
            },
        }),
        addKontaktorsak: (
            state: State,
            {
                offset,
                resetSubset,
                subsetName = "default",
                kontaktorsak = [],
            }: {
                resetSubset?: boolean;
                offset: number;
                subsetName: keyof Subsets;
                kontaktorsak: Kontaktorsak[];
            }
        ) =>
            subsetSetter<State, Kontaktorsak>({
                offset,
                subsetName,
                state,
                resetSubset,
                collectionName: moduleName,
                items: kontaktorsak,
            }),
        updateKontaktorsak: (
            state: State,
            {
                kontaktorsak,
                offset,
                subsetName,
            }: {
                kontaktorsak: Kontaktorsak[];
                offset: number;
                subsetName: keyof Subsets;
            } // TODO subsetName kommer skickas in i framtiden
        ) =>
            subsetUpdateSetter<State, Kontaktorsak>({
                offset,
                state,
                collectionName: moduleName,
                items: kontaktorsak,
            }),
    },
    actions: {
        ...baseActions({
            moduleName,
        }),
        getKontaktorsakById: async (
            {
                getters,
                commit,
                state,
            }: BaseActionsProps<State> & {
                getters: Getters;
            },
            {
                id,
                forceRefresh,
            }: {
                id: Kontaktorsak["id"];
                forceRefresh?: boolean;
            }
        ) => {
            const found = getters.kontaktorsakById({ id });

            if (!forceRefresh && found) {
                return found;
            }

            try {
                const { data } = await klinikenApi.get<{ kontaktorsak: Kontaktorsak }>(
                    `operation/kodverk/kontaktorsaker/${id}`,
                    getConfig()
                );

                if (data) {
                    commit("addKontaktorsak", {
                        offset: Object.keys(state.subsets.default.ids).length,
                        subsetName: "default",
                        kontaktorsak: [data],
                    });

                    return data;
                }

                return null;
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        fetchKontaktorsak: async (
            { commit, state }: BaseActionsProps<State>,
            {
                displayMode,
                filterParams = {},
                config = {},
            }: {
                offset?: number;
                filterParams?: FilterParams;
                config?: {
                    tabbnamn?: string;
                };
                displayMode?: boolean;
            }
        ) => {
            try {
                const {
                    subsetName,
                    results,
                    total,
                    nextPage,
                    offset: resultOffset,
                } = await fetchItems<State, Kontaktorsak>({
                    filterParams,
                    state,
                    config,
                    page: 1,
                    limit: 0,
                    collectionName: moduleName,
                    apiUrl: "operation/kodverk/kontaktorsaker/",
                });

                if (!results) {
                    return {
                        subsetName,
                        total,
                        nextPage,
                    };
                }

                commit("addKontaktorsak", {
                    subsetName,
                    offset: resultOffset,
                    kontaktorsak: results,
                });

                return {
                    total,
                    nextPage,
                    subsetName,
                    results: displayMode
                        ? results.map((singleResult) => convertKontaktorsakData({ kontaktorsak: singleResult }))
                        : results,
                };
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        async createKontaktorsak(
            { state, commit }: BaseActionsProps<State>,
            {
                kontaktorsak,
            }: {
                kontaktorsak: Kontaktorsak;
            }
        ): Promise<ActionReturn> {
            try {
                const { data } = await klinikenApi.post("operation/kodverk/kontaktorsaker/", kontaktorsak, getConfig());

                commit("addKontaktorsak", {
                    offset: Object.keys(state.subsets.default.ids).length,
                    subsetName: "default",
                    kontaktorsak: [data],
                });

                return data;
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        async updateKontaktorsak(
            { state, commit }: BaseActionsProps<State>,
            {
                kontaktorsak,
            }: {
                kontaktorsak: Kontaktorsak;
            }
        ): Promise<ActionReturn> {
            try {
                const { data } = await klinikenApi.put(
                    `operation/kodverk/kontaktorsaker/${kontaktorsak.id}/`,
                    kontaktorsak,
                    getConfig()
                );

                commit("updateKontaktorsak", {
                    offset: Object.keys(state.subsets.newlyCreated.ids).length,
                    subsetName: "newlyUpdated",
                    kontaktorsak: [data],
                });

                return data;
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
    },
};
